import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { useLanguage } from './contexts/LanguageContext';

export default function LoginSignup() {
	const [isLogin, setIsLogin] = useState(true);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [resetEmail, setResetEmail] = useState('');
	const [resetLoading, setResetLoading] = useState(false);
	const [resetMessage, setResetMessage] = useState('');

	const { login, signup, resetPassword } = useAuth();
	const navigate = useNavigate();
	const { t } = useLanguage();

	const handleSubmit = async (e) => {
		e.preventDefault();
		setMessage('');

		if (!isLogin && password !== confirmPassword) {
			return setMessage('Passwords do not match');
		}

		if (password.length < 6) {
			return setMessage('Password must be at least 6 characters');
		}

		setLoading(true);

		try {
			if (isLogin) {
				// Login existing user
				const userCredential = await login(email, password, rememberMe);

				// Use the user's UID as the person ID (they should match)
				const personId = userCredential.user.uid;
				navigate(`/${personId}`);
			} else {
				// Sign up new user
				const userCredential = await signup(email, password);
				console.log('User created successfully:', userCredential.user.uid);

				// Wait for the auth state to update and get a fresh token
				await userCredential.user.getIdToken(true);
				console.log('Authentication token refreshed');

				// Add a small delay to ensure auth context is properly set
				await new Promise((resolve) => setTimeout(resolve, 1000));

				// Create person profile for new user
				const createPersonProfile = httpsCallable(functions, 'createPersonProfile');
				try {
					console.log('Calling createPersonProfile with:', { email, firstName, lastName });
					console.log('Current user before call:', userCredential.user.uid);
					console.log('Auth token exists:', !!(await userCredential.user.getIdToken()));

					const result = await createPersonProfile({
						email,
						firstName,
						lastName,
					});
					console.log('Person profile created:', result.data);
					navigate(`/${result.data.personId}`);
				} catch (profileError) {
					console.error('Error creating person profile:', profileError);
					console.error(
						'Profile error details:',
						profileError.code,
						profileError.message,
						profileError.details
					);

					// Log the current user state for debugging
					console.log('Current user after error:', userCredential.user);
					console.log('User is authenticated:', !!userCredential.user);
					console.log(
						'User token after error:',
						await userCredential.user.getIdToken().catch(() => 'No token')
					);

					setMessage(`Profile creation failed: ${profileError.message}`);
					return;
				}
			}
		} catch (error) {
			let errorMessage = 'An error occurred';

			switch (error.code) {
				case 'auth/user-not-found':
					errorMessage = 'No account found with this email';
					break;
				case 'auth/wrong-password':
					errorMessage = 'Incorrect password';
					break;
				case 'auth/email-already-in-use':
					errorMessage = 'An account with this email already exists';
					break;
				case 'auth/weak-password':
					errorMessage = 'Password should be at least 6 characters';
					break;
				case 'auth/invalid-email':
					errorMessage = 'Invalid email address';
					break;
				default:
					errorMessage = error.message;
			}

			setMessage(errorMessage);
		}

		setLoading(false);
	};

	const handleForgotPassword = async (e) => {
		e.preventDefault();
		setResetMessage('');

		if (!resetEmail) {
			return setResetMessage('Please enter your email address');
		}

		setResetLoading(true);

		try {
			await resetPassword(resetEmail);
			setResetMessage('Password reset email sent! Check your inbox.');
			setTimeout(() => {
				setShowForgotPassword(false);
				setResetEmail('');
				setResetMessage('');
			}, 3000);
		} catch (error) {
			let errorMessage = 'An error occurred';

			switch (error.code) {
				case 'auth/user-not-found':
					errorMessage = 'No account found with this email address';
					break;
				case 'auth/invalid-email':
					errorMessage = 'Invalid email address';
					break;
				case 'auth/too-many-requests':
					errorMessage = 'Too many requests. Please try again later.';
					break;
				default:
					errorMessage = error.message;
			}

			setResetMessage(errorMessage);
		}

		setResetLoading(false);
	};

	return (
		<div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
			<div className='max-w-md w-full space-y-8'>
				<div>
					<h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>{t('appName')}</h2>
					<h4 className='mt-2 text-center text-sm text-blue-600'>{t('welcomeMessage')}</h4>
					<p className='mt-2 text-center text-sm text-gray-600'>
						{isLogin ? t('signInToYourAccount') : t('createYourAccount')}
					</p>
				</div>

				<form className='mt-8 space-y-6' onSubmit={handleSubmit}>
					<div className='space-y-4'>
						{!isLogin && (
							<>
								<div>
									<label htmlFor='firstName' className='block text-sm font-medium text-gray-700'>
										{t('firstName')}
									</label>
									<input
										id='firstName'
										name='firstName'
										type='text'
										required={!isLogin}
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										className='mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm'
									/>
								</div>
								<div>
									<label htmlFor='lastName' className='block text-sm font-medium text-gray-700'>
										{t('lastName')}
									</label>
									<input
										id='lastName'
										name='lastName'
										type='text'
										required={!isLogin}
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										className='mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm'
									/>
								</div>
							</>
						)}

						<div>
							<label htmlFor='email' className='block text-sm font-medium text-gray-700'>
								{t('emailAddress')}
							</label>
							<input
								id='email'
								name='email'
								type='email'
								autoComplete='email'
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className='mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm'
							/>
						</div>

						<div>
							<label htmlFor='password' className='block text-sm font-medium text-gray-700'>
								{t('password')}
							</label>
							<input
								id='password'
								name='password'
								type='password'
								autoComplete={isLogin ? 'current-password' : 'new-password'}
								required
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className='mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm'
							/>
						</div>

						{!isLogin && (
							<div>
								<label htmlFor='confirmPassword' className='block text-sm font-medium text-gray-700'>
									{t('confirmPassword')}
								</label>
								<input
									id='confirmPassword'
									name='confirmPassword'
									type='password'
									autoComplete='new-password'
									required={!isLogin}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									className='mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm'
								/>
							</div>
						)}
					</div>

					{isLogin && (
						<div className='flex items-center justify-between'>
							<div className='flex items-center'>
								<input
									id='rememberMe'
									name='rememberMe'
									type='checkbox'
									checked={rememberMe}
									onChange={(e) => setRememberMe(e.target.checked)}
									className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
								/>
								<label htmlFor='rememberMe' className='ml-2 block text-sm text-gray-900'>
									{t('rememberMe')}
								</label>
							</div>
							<div>
								<button
									type='button'
									onClick={() => setShowForgotPassword(true)}
									className='text-sm text-blue-600 hover:text-blue-500'
								>
									{t('forgotPassword')}
								</button>
							</div>
						</div>
					)}

					{message && <div className='text-red-600 text-sm text-center'>{message}</div>}

					<div>
						<button
							type='submit'
							disabled={loading}
							className='group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
						>
							{loading ? t('processing') : isLogin ? t('signIn') : t('signUp')}
						</button>
					</div>

					<div className='text-center'>
						<button
							type='button'
							onClick={() => {
								setIsLogin(!isLogin);
								setMessage('');
								setFirstName('');
								setLastName('');
								setConfirmPassword('');
							}}
							className='text-blue-600 hover:text-blue-500 text-sm'
						>
							{isLogin ? t('dontHaveAccountSignUp') : t('alreadyHaveAccountSignIn')}
						</button>
					</div>
				</form>
			</div>

			{/* Forgot Password Modal */}
			{showForgotPassword && (
				<div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
					<div className='relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white'>
						<div className='mt-3'>
							<div className='flex items-center justify-between mb-4'>
								<h3 className='text-lg font-medium text-gray-900'>{t('resetPassword')}</h3>
								<button
									onClick={() => {
										setShowForgotPassword(false);
										setResetEmail('');
										setResetMessage('');
									}}
									className='text-gray-400 hover:text-gray-600'
								>
									<svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth='2'
											d='M6 18L18 6M6 6l12 12'
										></path>
									</svg>
								</button>
							</div>

							<form onSubmit={handleForgotPassword}>
								<div className='mb-4'>
									<label
										htmlFor='resetEmail'
										className='block text-sm font-medium text-gray-700 mb-2'
									>
										{t('emailAddress')}
									</label>
									<input
										id='resetEmail'
										name='resetEmail'
										type='email'
										required
										value={resetEmail}
										onChange={(e) => setResetEmail(e.target.value)}
										className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500'
									/>
								</div>

								{resetMessage && (
									<div
										className={`text-sm text-center mb-4 ${
											resetMessage.includes('sent') ? 'text-green-600' : 'text-red-600'
										}`}
									>
										{resetMessage}
									</div>
								)}

								<div className='flex justify-end space-x-3'>
									<button
										type='button'
										onClick={() => {
											setShowForgotPassword(false);
											setResetEmail('');
											setResetMessage('');
										}}
										className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'
									>
										{t('cancel')}
									</button>
									<button
										type='submit'
										disabled={resetLoading}
										className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
									>
										{resetLoading ? t('sending') : t('sendResetEmail')}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
